// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps, LinkProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { BreakpointKey } from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ChevronDown, { ChevronDownOffset } from "@store/Library/Components/Toolkit/Icons/ChevronDown";
import { headerIconStyles } from "@store/Themes/common/mixins/header";
import { linkStyles } from "@store/Themes/common/mixins/link";
import useExpandProps, { ExpandStyles } from "@toolkit/Framework/Common/Hooks/useExpandProps";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
import breakpointValues from "@toolkit/Framework/Common/Utilities/breakpointValues";
import getBreakpointIndex from "@toolkit/Framework/Common/Utilities/getBreakpointIndex";
import getPreviousBreakpoint from "@toolkit/Framework/Common/Utilities/getPreviousBreakpoint";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { IconOffset } from "@toolkit/Framework/Types/Icon";
import { iconScalableStyles, visuallyHiddenStyles } from "@toolkit/Themes/common/mixins";
import React, { FC, useMemo } from "react";
import { css, useTheme } from "styled-components";

const baseTextStyles = () => css`
    display: inline-block;
    vertical-align: top;
    max-width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

export interface HeaderLinkProps extends LinkProps, ExpandStyles<HeaderLinkStyles> {
    iconSrc?: IconPresentationProps["src"];
    iconOffset?: IconOffset;
    showIconOnly?: boolean;
    label?: string;
    text?: string;
    isDropdown?: boolean;
    breakpoint?: BreakpointKey;
}

export interface HeaderLinkStyles {
    link?: LinkPresentationProps;
    innerWrapper?: InjectableCss;
    label?: TypographyPresentationProps;
    textWrapper?: InjectableCss;
    text?: TypographyPresentationProps;
    iconDropdown?: IconPresentationProps;
}

export const headerLinkStyles: HeaderLinkStyles = {
    link: {
        css: css`
            ${linkStyles("inherit", "header.hover", false, true)}
            ${headerIconStyles()}
            position: relative;
            font-size: ${x => x.theme.design.typography.size.smaller};
            line-height: ${x => x.theme.design.typography.lineHeight.small};
            min-height: ${pxToRem(24) /* Store Custom Code - fixes an alignment issue */};

            &[aria-expanded="true"] {
                color: ${getColor("header.hover")};
                text-decoration: underline;
            }
        `,
    },
    innerWrapper: {
        css: css`
            display: block;
            max-width: ${pxToRem(125)};
            margin-left: ${pxToRem(6) /* Store Custom Code */};
        `,
    },
    label: {
        css: baseTextStyles(),
    },
    textWrapper: {
        css: css`
            display: flex;
            align-items: center;
        `,
    },
    text: {
        css: css`
            ${baseTextStyles()}
            display: block;
            font-weight: ${x => x.theme.design.typography.weight.base /* Store Custom Code */};
        `,
    },
    iconDropdown: {
        src: ChevronDown,
        css: css`
            ${iconScalableStyles("1em", ChevronDownOffset)}
            margin-left: 0.5em;
        `,
    },
};

const HeaderLink: FC<HeaderLinkProps> = ({
    children,
    breakpoint: baseBreakpoint,
    iconSrc,
    iconOffset,
    showIconOnly,
    label,
    text,
    isDropdown,
    extendedStyles,
    overridedStyles,
    ...linkProps
}) => {
    const baseStyles = useExpandProps(headerLinkStyles, extendedStyles, overridedStyles);
    const theme = useTheme();
    const _breakpoint = showIconOnly ? null : baseBreakpoint || theme.design.header.breakpoint;

    const styles = useMemo(() => {
        const breakpointIndex = _breakpoint ? getBreakpointIndex(_breakpoint, theme) : 9999;
        let lastSize: string;
        const iconSizes = theme.breakpoints.keys.map((key, index) => {
            const size = theme.design.header.iconButton.size[index];

            if (!size && !lastSize) {
                return size;
            }

            if (size) {
                lastSize = size;
            }

            return iconScalableStyles(size || lastSize, index >= breakpointIndex ? iconOffset : undefined);
        });

        return mergeProps<HeaderLinkStyles>(baseStyles, {
            link: {
                icon: {
                    iconProps: {
                        src: iconSrc,
                        css: css`
                            ${breakpointValues(iconSizes)}
                        `,
                    },
                },
            },
            innerWrapper: {
                css: css`
                    /* Store Custom Code - Start */
                    display: flex;
                    flex-direction: column;
                    /* Store Custom Code - End */

                    ${() => {
                        if (!_breakpoint) {
                            return visuallyHiddenStyles();
                        }

                        return css`
                            ${breakpoint(getPreviousBreakpoint(_breakpoint, theme), "down")} {
                                ${visuallyHiddenStyles()}
                            }
                        `;
                    }}
                `,
            },
        });
    }, [baseStyles, iconSrc, iconOffset, _breakpoint]);

    return (
        <Link {...linkProps} {...styles.link}>
            <StyledWrapper as="span" {...styles.innerWrapper}>
                {Boolean(label) && <Typography {...styles.label}>{label}</Typography> /* Store Custom Code */}
                <StyledWrapper as="span" {...styles.textWrapper}>
                    <Typography {...styles.text}>{text}</Typography>
                    {isDropdown && <Icon {...styles.iconDropdown} />}
                </StyledWrapper>
            </StyledWrapper>
            {children}
        </Link>
    );
};

export default HeaderLink;
